import React, { useEffect, useState } from "react";
import { getCourses } from "../../API/courses";
import { getBatches } from "../../API/Batches";
import FormControl from "@mui/material/FormControl";
import { createStudent, updateKid } from "../../API/kids-mgmt";
import "./addNewKids.css"; // Replace with new CSS file
import { ERROR_TOAST, SUCCESS_TOAST } from "../../helpers/toasters";
import { Autocomplete, Checkbox, TextField, Chip } from "@mui/material";

interface CourseType {
  _id: string;
  name: string;
  batches: BatchType[];
}

interface BatchType {
  _id: string;
  name: string;
}

interface handelFuncProps {
  handelClose: () => void;
  data?: {
    unique_id: Number;
    kid_name: string[];
    dob?: string; // Add DOB to incoming props
    gender: string;
    parents_name: string;
    contact: string;
    interests: string[];
    courses: string[];
    batches: string[];
    school: string;
    city: string;
    name: string;
    __v?: Number;
    _id: string;
  };
}

export default function AddNewKids(props: handelFuncProps) {
  const [name, setName] = useState<string>(props.data?.name || "");
  const [dateOfBirth, setDateOfBirth] = useState<string>(
    props.data?.dob || new Date().toISOString().split("T")[0] // Default to today if no DOB
  );
  const [mobile, setMobile] = useState(props.data?.contact || "");
  const [city, setCity] = useState<string>(props.data?.city || "");
  const [parents, setParents] = useState(props.data?.parents_name || "");
  const [Gender, setGender] = useState<string>(props.data?.gender || "male");
  const [schoolName, setSchoolName] = useState(props.data?.school || "");

  const [courseRow, setCourseRow] = useState<CourseType[]>([]);
  const [course, setCourse] = useState<CourseType[]>([]);
  const [batch, setBatch] = useState<BatchType[]>([]);
  const [allBatches, setAllBatches] = useState<BatchType[]>([]);

  const batchColors = [
    "#FF6B6B", "#4ECDC4", "#FFD166", "#06D6A0", "#118AB2",
    "#7209B7", "#3A86FF", "#FB5607", "#8338EC", "#FFBE0B"
  ];

  const handleClick = () => props.handelClose();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesData, batchesData] = await Promise.all([getCourses(), getBatches()]);
        
        const transformedCourses = coursesData.map((course: any) => ({
          ...course,
          batches: course.batches
            ?.map((batchId: string) => batchesData.find((b: BatchType) => b._id === batchId))
            .filter(Boolean) || [],
        }));
        

        setCourseRow(transformedCourses);
        setAllBatches(batchesData);

        
        const courseIds = props.data?.courses.map((c: any) => c._id) || [];
        
        const batchIds = props.data?.batches.map((b: any) => (typeof b === "string" ? b : b._id)) || [];
        

        if (courseIds.length) {
          const selectedCourses = transformedCourses.filter((course) =>
            courseIds.includes(course._id)
          );
          setCourse(selectedCourses);
        }

        if (batchIds.length) {
          const selectedBatches = batchesData.filter((batch) =>
            batchIds.includes(batch._id)
          );
          setBatch(selectedBatches);
        }
      } catch (error) {
        console.error("Data fetching error:", error);
        ERROR_TOAST("Error fetching courses and batches");
      }
    };
    fetchData();
  }, [props.data]);

  useEffect(() => {
    if (course.length > 0) {
      const newAvailableBatches = course.flatMap((c) => c.batches || []);
      setAllBatches(newAvailableBatches);
    }
  }, [course, courseRow]);

  const availableBatches = course.length > 0
    ? course.flatMap((c) => c.batches || []).filter((batch, index, self) =>
      index === self.findIndex((b) => b && b._id === batch._id))
    : allBatches;  // Show all batches if no course is selected

  const handleCourseChange = (newCourses: CourseType[]) => {
    setCourse(newCourses);
    const newAvailableBatches = newCourses.flatMap((c) => c.batches);
    const filteredBatches = batch.filter((b) =>
      newAvailableBatches.some((ab) => ab._id === b._id)
    );
    setBatch(filteredBatches);
  };

  const getBatchColor = (batchId: string) => {
    const hashCode = batchId.split('').reduce(
      (hash, char) => char.charCodeAt(0) + ((hash << 5) - hash), 0
    );
    return batchColors[Math.abs(hashCode) % batchColors.length];
  };

  const renderBatchTags = () => {
    if (!batch.length) return null;
    return (
      <div className="batch-tags-container" style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '10px',
        marginBottom: '15px'
      }}>
        <p style={{ fontWeight: 'bold', margin: '0', marginRight: '8px', alignSelf: 'center' }}>
          Current Batches:
        </p>
        {batch.map((b) => (
          <Chip
            key={b._id}
            label={b.name}
            style={{
              backgroundColor: getBatchColor(b._id),
              color: 'white',
              fontWeight: 'bold',
              padding: '2px 0',
              borderRadius: '16px',
              animation: 'fadeIn 0.3s ease-in',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onDelete={() => setBatch(batch.filter((item) => item._id !== b._id))}
          />
        ))}
      </div>
    );
  };


  const showData = async () => {
    if (!name || name.length < 3) {
      return ERROR_TOAST("Please enter a valid name!");
    }
    if (!dateOfBirth) {
      return ERROR_TOAST("Please enter the date of birth!");
    }
    if (!mobile || mobile.length < 10) {
      return ERROR_TOAST("Please enter a valid mobile number!");
    }

    const data = {
      name,
      gender: Gender,
      dob: dateOfBirth, // Send DOB instead of age
      courses: course.map((c) => c._id),
      batches: batch.map((b) => b._id),
      contact: mobile,
      school: schoolName,
      parents_name: parents,
      city,
    };

    const result = await createStudent(data);
    if (result.status === "failed") {
      return ERROR_TOAST(result.message);
    }
    if (result.message === "Phone number already linked with another account") {
      return ERROR_TOAST(result.message);
    }
    if (result.response.message.includes("successfully")) {
      SUCCESS_TOAST("Kid Profile Created successfully");
      setTimeout(() => handleClick(), 2000);
    }
  };

  const updateData = async () => {
    if (!name || name.length < 3) {
      return ERROR_TOAST("Please enter a valid name!");
    }
    if (!dateOfBirth) {
      return ERROR_TOAST("Please enter the date of birth!");
    }
    if (!mobile || mobile.length < 10) {
      return ERROR_TOAST("Please enter a valid mobile number!");
    }

    const data = {
      _id: props.data?._id,
      name,
      gender: Gender,
      dob: dateOfBirth, // Send DOB instead of age
      contact: mobile,
      school: schoolName,
      courses: course.map((c) => c._id),
      batches: batch.map((b) => b._id),
      parents_name: parents,
      city,
    };

    try {
      const result = await updateKid(data);
      if (result.status === "success") {
        SUCCESS_TOAST("Kid Profile Updated successfully");
        setTimeout(() => handleClick(), 2000);
      } else {
        ERROR_TOAST(result.message || "Update failed");
      }
    } catch (error) {
      console.error("Update Error:", error);
      ERROR_TOAST("An error occurred while updating");
    }
  };

  return (
    <div className="addKids">
      <div className="fullDesc">
        <div className="headerAK">
          <h2>{props.data ? "Update Kid" : "Add Student"}</h2>
          <div className="top-svg" onClick={handleClick}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#8E8EA1"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <div className="ad-body">
          {/* Personal Information Section */}
          <div className="form-section">
            <div className="form-group">
              <label className="form-label" htmlFor="kid-name">Student's Name*</label>
              <input
                id="kid-name"
                className="form-control"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter Student's name"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="dob">Date of Birth*</label>
              <input
                id="dob"
                type="date"
                className="form-control"
                name="dob"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    checked={Gender === "male"}
                    onChange={(e) => setGender(e.target.value)}
                  />
                  <span>Male</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    checked={Gender === "female"}
                    onChange={(e) => setGender(e.target.value)}
                  />
                  <span>Female</span>
                </label>
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="form-section">
            <div className="form-group">
              <label className="form-label" htmlFor="parents-name">Parent's Name</label>
              <input
                id="parents-name"
                className="form-control"
                name="parents_name"
                value={parents}
                onChange={(e) => setParents(e.target.value)}
                placeholder="Enter parent's name"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="mobile">Mobile Number*</label>
              <input
                id="mobile"
                type="number"
                className="form-control"
                name="mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="Enter mobile number"
                maxLength={10}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="city">City</label>
              <input
                id="city"
                type="text"
                className="form-control"
                name="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter city"
              />
            </div>
          </div>

          {/* Education Section */}
          <div className="form-section">
            <div className="form-group">
              <label className="form-label" htmlFor="school">School Name</label>
              <input
                id="school"
                type="text"
                className="form-control"
                name="school"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                placeholder="Enter school name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Courses</label>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  options={courseRow}
                  value={course}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name}
                  onChange={(_, newValue) => handleCourseChange(newValue)}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option._id}>
                      <Checkbox checked={selected} />
                      {option.name}
                    </li>
                  )}
                  renderTags={() => null} // Don't render chips inside the input
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={course.length === 0 ? "Search courses" : `${course.length} courses selected`}
                      variant="outlined"
                    />
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />
              </FormControl>
            </div>
            <div className="form-group">
              <label className="form-label">Batches</label>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  options={availableBatches}
                  value={batch}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name}
                  onChange={(_, newValue) => setBatch(newValue)}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option._id}>
                      <Checkbox checked={selected} />
                      {option.name}
                    </li>
                  )}
                  renderTags={() => null} // Don't render chips inside the input
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={batch.length === 0 ? "Search batches" : `${batch.length} batches selected`}
                      variant="outlined"
                    />
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />
              </FormControl>
            </div>
          </div>
          {props.data && renderBatchTags()}
          <style>
            {`
              @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
              @keyframes pulse { 0% { transform: scale(1); } 50% { transform: scale(1.05); } 100% { transform: scale(1); } }
              .batch-tags-container:hover .MuiChip-root { animation: pulse 1s infinite; animation-delay: calc(var(--index) * 0.1s); }
            `}
          </style>
        </div>
        <div className="drag-drop-top-line"></div>
        <div className="down-buttons d-flex">
          <button
            className="create-button"
            id="submit"
            onClick={() => (props.data ? updateData() : showData())}
          >
            {props.data ? "Update Kid" : "Add kid"}
          </button>
          <button className="cancel-button" onClick={handleClick}>Cancel</button>
        </div>
      </div>
    </div>
  );
}